import { createSelector } from 'reselect';

import {
  MODE,
  STATUS,
  BRANDS,
  AUTHORS,
  SENDERS,
  FOLLOW_UP_STATUS as FOLLOW_UP_STATUS_FILTER,
  START_DATE_TO,
  INTERVENTION_STATUS,
  ADVISOR_USERS,
} from 'constants/FilterConstants';
import { PaginationType } from 'constants/Global';
import { DATE_TYPE, DEFAULT_STATUS } from 'constants/InterventionConstants';
import { getModulesByMode, MODULES } from 'constants/ModuleConstants';
import { FOLLOW_UP_STATUS, FOLLOW_UP_STATUSES } from 'modules/followUp/constant';
import { getByIds as getInterventionByIds } from 'modules/interventions/selectors';
import { getCurrentUserIsReadOnly } from 'modules/users/selectors/currentUser';
import { getSelectedDealerId } from 'selectors/dealerSelectors';
import { isElementBefore } from 'utils/ArrayUtils';
import { indexByProp } from 'utils/global';
import { uniq } from 'utils/ObjectUtils';
import { cleanPayload } from 'utils/RequestUtils';

import { getFollowUpById, getFollowUpByIds, getRoot, getUIState } from '.';

const getDashboardState = createSelector(getUIState, ui => ui.dashboard);

const getDataDashboard = createSelector(getDashboardState, state => state.data);

const getFollowUpIds = createSelector(getDataDashboard, state => state.ids);

const getFollowUps = createSelector(getRoot, getFollowUpIds, getFollowUpByIds);

const getFollowUpsInterventionIds = createSelector(getFollowUps, followUps =>
  followUps.reduce((acc, { interventionId, partsInterventionId }) => {
    const entityId = interventionId ?? partsInterventionId;
    return uniq([...acc, entityId]);
  }, []));

const getFollowUpsInterventions = createSelector(getRoot, getFollowUpsInterventionIds, getInterventionByIds);

export const getDashboardFollowUps = createSelector(
  getFollowUps,
  getFollowUpsInterventions,
  (followUps, interventions) => {
    const dictionary = indexByProp('id', interventions);
    return followUps.reduce((acc, followUp) => {
      const linkedEntityId = followUp.interventionId ?? followUp.partsInterventionId;
      return [...acc, { ...followUp, intervention: dictionary[linkedEntityId] }];
    }, []);
  }
);

export const getPage = createSelector(getDataDashboard, state => state.page);

export const loading = createSelector(getDataDashboard, state => state.loading);

export const getFiltersState = createSelector(getDashboardState, dashboard => dashboard.filters);

export const getMode = createSelector(getFiltersState, state => state.mode);

export const getFollowUpDealerExtensions = createSelector(
  getMode,
  mode => {
    const modules = getModulesByMode(mode);

    return uniq(
      modules.reduce((acc, module) => {
        const { extensions = [] } = MODULES[module];
        return [...acc, ...extensions];
      }, [])
    );
  }
);

export const getFilters = createSelector(getFiltersState, getMode, (filtersByMode, mode) => ({
  [MODE]: mode,
  ...filtersByMode[mode],
}));

export const getDashboardRequestParams = createSelector(
  getMode,
  getSelectedDealerId,
  getPage,
  getFilters,
  (
    mode,
    dealerId,
    { size, number: page },
    { period, [BRANDS]: brands, [AUTHORS]: authors, [SENDERS]: senders, [FOLLOW_UP_STATUS_FILTER]: status, [ADVISOR_USERS]: advisors, ...filters }
  ) => ({
    size,
    page,
    mode,
    dealerId,
    paginationType: PaginationType.SLICE,
    ...cleanPayload(filters),
    [START_DATE_TO]: period === DATE_TYPE.CURRENT ? new Date().toISOString() : filters[START_DATE_TO],
    [STATUS]: status,
    [INTERVENTION_STATUS]: DEFAULT_STATUS,
    [BRANDS]: brands.map(({ id }) => id),
    [AUTHORS]: authors.map(({ id }) => id),
    [SENDERS]: senders.map(({ id }) => id),
    [ADVISOR_USERS]: advisors.map(({ id }) => id),
  })
);

const getLoadingState = createSelector(getDashboardState, dashboard => dashboard.loading);

export const isScheduling = createSelector(getLoadingState, ({ scheduling }) => scheduling);

export const allowFollowUpActions = createSelector(
  getFollowUpById,
  getCurrentUserIsReadOnly,
  (followUp, readonly) => !readonly && (!followUp || isElementBefore(FOLLOW_UP_STATUSES, followUp.status, FOLLOW_UP_STATUS.SENT))
);

export const canSendFollowUp = createSelector(
  getFollowUpById,
  getCurrentUserIsReadOnly,
  (followUp, readonly) => !readonly && followUp && followUp.isSendable && isElementBefore(FOLLOW_UP_STATUSES, followUp.status, FOLLOW_UP_STATUS.SENT)
);
